import { create } from 'zustand';
import type { DashboardStats, Agency, Tenant, Application, User } from '../types/admin';

interface AdminState {
  // Dashboard stats
  stats: DashboardStats | null;
  setStats: (stats: DashboardStats) => void;

  // Agencies
  agencies: Agency[];
  setAgencies: (agencies: Agency[]) => void;
  addAgency: (agency: Agency) => void;
  updateAgency: (id: string, agency: Partial<Agency>) => void;
  removeAgency: (id: string) => void;

  // Tenants
  tenants: Tenant[];
  setTenants: (tenants: Tenant[]) => void;
  addTenant: (tenant: Tenant) => void;
  updateTenant: (id: string, tenant: Partial<Tenant>) => void;
  removeTenant: (id: string) => void;

  // Applications
  applications: Application[];
  setApplications: (applications: Application[]) => void;
  addApplication: (application: Application) => void;
  updateApplication: (id: string, application: Partial<Application>) => void;
  removeApplication: (id: string) => void;

  // Users
  users: User[];
  setUsers: (users: User[]) => void;
  addUser: (user: User) => void;
  updateUser: (id: string, user: Partial<User>) => void;
  removeUser: (id: string) => void;

  // UI State
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
}

export const useAdminStore = create<AdminState>((set) => ({
  // Dashboard stats
  stats: null,
  setStats: (stats) => set({ stats }),

  // Agencies
  agencies: [],
  setAgencies: (agencies) => set({ agencies }),
  addAgency: (agency) => set((state) => ({ agencies: [...state.agencies, agency] })),
  updateAgency: (id, agency) =>
    set((state) => ({
      agencies: state.agencies.map((a) => (a.id === id ? { ...a, ...agency } : a)),
    })),
  removeAgency: (id) => set((state) => ({ agencies: state.agencies.filter((a) => a.id !== id) })),

  // Tenants
  tenants: [],
  setTenants: (tenants) => set({ tenants }),
  addTenant: (tenant) => set((state) => ({ tenants: [...state.tenants, tenant] })),
  updateTenant: (id, tenant) =>
    set((state) => ({
      tenants: state.tenants.map((t) => (t.id === id ? { ...t, ...tenant } : t)),
    })),
  removeTenant: (id) => set((state) => ({ tenants: state.tenants.filter((t) => t.id !== id) })),

  // Applications
  applications: [],
  setApplications: (applications) => set({ applications }),
  addApplication: (application) =>
    set((state) => ({ applications: [...state.applications, application] })),
  updateApplication: (id, application) =>
    set((state) => ({
      applications: state.applications.map((a) => (a.id === id ? { ...a, ...application } : a)),
    })),
  removeApplication: (id) =>
    set((state) => ({ applications: state.applications.filter((a) => a.id !== id) })),

  // Users
  users: [],
  setUsers: (users) => set({ users }),
  addUser: (user) => set((state) => ({ users: [...state.users, user] })),
  updateUser: (id, user) =>
    set((state) => ({
      users: state.users.map((u) => (u.id === id ? { ...u, ...user } : u)),
    })),
  removeUser: (id) => set((state) => ({ users: state.users.filter((u) => u.id !== id) })),

  // UI State
  sidebarOpen: true,
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
}));
